import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * BrandyAI Context
 * Global state management for contextual Brandy AI right rail
 */

const BrandyAIContext = createContext({
  isOpen: false,
  domainContext: null,
  logoFixContext: null,
  conversationHistory: [],
  domains: [],
  openRail: () => {},
  openLogoFixRail: () => {},
  closeRail: () => {},
  updateContext: () => {},
  addToHistory: () => {},
  clearHistory: () => {},
  setDomains: () => {},
});

/**
 * BrandyAI Provider
 * Wraps the app to provide global access to Brandy AI state
 */
export const BrandyAIProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [domainContext, setDomainContext] = useState(null);
  const [logoFixContext, setLogoFixContext] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [domains, setDomains] = useState([]);

  /**
   * Open the rail with domain context
   * @param {Object} context - Domain context object
   * @param {string} context.domainId - Domain ID
   * @param {string} context.domainName - Domain name
   * @param {number} context.healthScore - Health score
   * @param {Array} context.topIssues - Top issues
   * @param {Object} context.fullDomainData - Complete domain data
   * @param {string} context.initialQuestion - Pre-populated question (optional)
   * @param {boolean} context.autoSubmit - Auto-submit the question (optional, default: false)
   */
  const openRail = useCallback((context) => {
    // Sanitize context (strip secrets, redact sensitive data)
    const sanitizedContext = {
      ...context,
      initialQuestion: context.initialQuestion || null,
      autoSubmit: context.autoSubmit || false,
      fullDomainData: context.fullDomainData
        ? {
            ...context.fullDomainData,
            // Strip any potential secrets
            apiKeys: undefined,
            privateKeys: undefined,
            passwords: undefined,
          }
        : null,
    };

    setDomainContext(sanitizedContext);
    setIsOpen(true);

    // Log for debugging
    console.log('[BrandyAI] Rail opened with context:', {
      domainName: sanitizedContext.domainName,
      healthScore: sanitizedContext.healthScore,
      issueCount: sanitizedContext.topIssues?.length || 0,
      hasInitialQuestion: !!sanitizedContext.initialQuestion,
      autoSubmit: sanitizedContext.autoSubmit,
    });
  }, []);

  /**
   * Open the rail with logo fix context
   * @param {Object} context - Logo fix context object
   * @param {string} context.invalidLogoSvg - The invalid SVG content
   * @param {Function} context.onLogoFixed - Callback function to receive fixed logo
   */
  const openLogoFixRail = useCallback((context) => {
    setLogoFixContext({
      invalidLogoSvg: context.invalidLogoSvg,
      onLogoFixed: context.onLogoFixed,
    });
    setIsOpen(true);

    console.log('[BrandyAI] Logo fix rail opened');
  }, []);

  /**
   * Close the rail
   */
  const closeRail = useCallback(() => {
    setIsOpen(false);
    setLogoFixContext(null);
    console.log('[BrandyAI] Rail closed');
  }, []);

  /**
   * Update existing context (for switching domains)
   * @param {Object} context - New domain context
   */
  const updateContext = useCallback((context) => {
    const sanitizedContext = {
      ...context,
      fullDomainData: context.fullDomainData
        ? {
            ...context.fullDomainData,
            apiKeys: undefined,
            privateKeys: undefined,
            passwords: undefined,
          }
        : null,
    };

    setDomainContext(sanitizedContext);

    console.log('[BrandyAI] Context updated:', {
      domainName: sanitizedContext.domainName,
      healthScore: sanitizedContext.healthScore,
    });
  }, []);

  /**
   * Add message to conversation history
   * @param {Object} message - Message object { role: 'user'|'assistant', content: string }
   */
  const addToHistory = useCallback((message) => {
    setConversationHistory((prev) => [...prev, { ...message, timestamp: Date.now() }]);
  }, []);

  /**
   * Clear conversation history
   */
  const clearHistory = useCallback(() => {
    setConversationHistory([]);
    console.log('[BrandyAI] Conversation history cleared');
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      domainContext,
      logoFixContext,
      conversationHistory,
      domains,
      openRail,
      openLogoFixRail,
      closeRail,
      updateContext,
      addToHistory,
      clearHistory,
      setDomains,
    }),
    [
      isOpen,
      domainContext,
      logoFixContext,
      conversationHistory,
      domains,
      openRail,
      openLogoFixRail,
      closeRail,
      updateContext,
      addToHistory,
      clearHistory,
      setDomains,
    ]
  );

  return <BrandyAIContext.Provider value={value}>{children}</BrandyAIContext.Provider>;
};

/**
 * Custom hook to use Brandy AI context
 * @returns {Object} Brandy AI context
 */
export const useBrandyAI = () => {
  const context = useContext(BrandyAIContext);

  if (context === undefined) {
    throw new Error('useBrandyAI must be used within a BrandyAIProvider');
  }

  return context;
};

export default BrandyAIContext;
